import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { waLink } from "@/lib/whatsapp";

const SERVICES = [
  {
    id: "mutual-funds",
    title: "Mutual Funds & SIP",
    image:
      "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHw0fHxncm93aW5nJTIwcGxhbnR8ZW58MHx8fHwxNzg3NTQ4ODk1fDA&ixlib=rb-4.1.0&q=85",
    alt: "Hands nurturing a seedling, symbolising SIP wealth growth",
    desc: "Disciplined, goal-based investing in equity and debt mutual funds — built around your timeline, not market noise.",
    points: ["Start a SIP from just ₹500/month", "Goal planning for home, education & retirement", "Regular portfolio reviews & rebalancing"],
    tall: true,
  },
  {
    id: "fixed-income",
    title: "Fixed Income",
    image:
      "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Coins in a glass jar, symbolising stable fixed income savings",
    desc: "Predictable, steady returns for capital you cannot afford to risk.",
    points: ["Fixed deposits, bonds & government schemes", "Capital protection first, returns second", "Ideal for retirees & conservative savers"],
  },
  {
    id: "term-insurance",
    title: "Term Insurance",
    image:
      "https://images.unsplash.com/photo-1622610607501-32ac9c927216?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxmYW1pbHklMjBzdW5zZXR8ZW58MHx8fHwxNzg3NTQ4ODk1fDA&ixlib=rb-4.1.0&q=85",
    alt: "Family walking together at sunset, symbolising protection",
    desc: "Pure protection that keeps your family's dreams safe, even when you're not around.",
    points: ["High life cover at surprisingly low premiums", "Income replacement for your loved ones", "End-to-end claim assistance for family"],
  },
];

export const Services = () => (
  <section id="services" className="py-20 lg:py-32 scroll-mt-20">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl"
      >
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-bronze">What we do</p>
        <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-forest">
          Three pillars of a secure financial life
        </h2>
      </motion.div>

      <div className="mt-12 lg:mt-16 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {SERVICES.map((s, i) => (
          <motion.article
            key={s.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            data-testid={`service-card-${s.id}`}
            className="group bg-white border border-border rounded-2xl overflow-hidden transition-transform duration-300 hover:-translate-y-1 hover:border-forest/40 flex flex-col"
          >
            <div className={`overflow-hidden ${s.tall ? "h-56 lg:h-64" : "h-48 lg:h-56"}`}>
              <img
                src={s.image}
                alt={s.alt}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="p-6 lg:p-8 flex flex-col flex-1">
              <h3 className="font-serif text-2xl font-medium text-forest">{s.title}</h3>
              <p className="mt-3 text-sm text-muted-foreground">{s.desc}</p>
              <ul className="mt-5 space-y-2 flex-1">
                {s.points.map((p) => (
                  <li key={p} className="flex gap-2 text-sm text-foreground/80">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-bronze" />
                    {p}
                  </li>
                ))}
              </ul>
              <a
                href={waLink(`Hi Rajkiran Wealth, I am interested in ${s.title}. Please call me back.`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`service-whatsapp-link-${s.id}`}
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-forest transition-colors hover:text-bronze"
              >
                Enquire on WhatsApp
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);
